import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { copy } from "../copy";
import { login, safeNext } from "../auth/api";
import AuthField from "../auth/AuthField";
import AuthShell, { AuthAlert } from "../auth/AuthShell";
import GoogleButton from "../auth/GoogleButton";
import Icon from "../components/Icon";
import { fullPageLoad } from "../navigate";
import { useSession } from "../session";

const t = copy.auth.login;

// Signing in ends in a full page load, because most `next` targets are Go
// pages and the app re-reads the session on load.
export default function Login() {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const session = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (session.kind !== "visitor") fullPageLoad(next);
  }, [session.kind, next]);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError(t.missing);
      return;
    }
    setSubmitting(true);
    const result = await login(email.trim(), password);
    if (result.ok) {
      fullPageLoad(next);
      return;
    }
    setError(result.error);
    setSubmitting(false);
  };

  return (
    <AuthShell
      title={t.title}
      eyebrow={t.eyebrow}
      heading={t.heading}
      sub={t.sub}
      footer={
        <>
          {t.footer}{" "}
          <Link to={`/register${params.get("next") ? `?next=${encodeURIComponent(next)}` : ""}`} className="bf-focusable font-bold text-gold hover:text-gold-light">
            {t.footerLink}
          </Link>
        </>
      }
    >
      <GoogleButton onDone={() => fullPageLoad(next)} onError={setError} />
      <form onSubmit={submit} noValidate className="flex flex-col gap-5">
        {error && <AuthAlert message={error} />}
        <AuthField
          id="email"
          label={copy.auth.email}
          icon="mail"
          type="email"
          autoComplete="email"
          value={email}
          placeholder={copy.auth.emailPlaceholder}
          onChange={setEmail}
        />
        <AuthField
          id="password"
          label={copy.auth.password}
          icon="lock"
          type="password"
          revealable
          autoComplete="current-password"
          value={password}
          placeholder={t.passwordPlaceholder}
          onChange={setPassword}
        />
        <div className="flex justify-end">
          <a
            href="/forgetpassword"
            className="bf-focusable text-xs font-bold uppercase tracking-[0.12em] text-gold hover:text-gold-light"
          >
            {t.forgot}
          </a>
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="bf-focusable mt-1 flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-money text-sm font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? t.submitting : t.submit}
          {!submitting && <Icon name="arrowRight" className="text-base" />}
        </button>
      </form>
    </AuthShell>
  );
}
